import { HttpRequestWrapper } from "../HttpRequestWrapper";
import { Logger } from "../Logger";
import {
    SharePointDriveLocation,
    SharePointFolderItem,
    SharePointFolderPage,
} from "../../models/projects/SharePointFolderItem";
import { ProjectFolderConnection } from "../../models/projects/ProjectSettingsResponse";
import { SharePointAuthService } from "./SharePointAuthService";

/** Reason a SharePoint Graph request ultimately failed. */
export type SharePointGraphErrorReason =
    | "forbidden"
    | "notFound"
    | "throttled"
    | "network"
    | "notConfigured"
    | "unknown";

/** Typed error surfaced by SharePointGraphService so callers don't need to know about Graph's raw error shapes. */
export class SharePointGraphError extends Error {
    constructor(message: string, public readonly reason: SharePointGraphErrorReason) {
        super(message);
        // Required for correct instanceof checks when TypeScript targets ES5.
        Object.setPrototypeOf(this, SharePointGraphError.prototype);
    }
}

/** Sort options understood by the Project Files browser, translated to Graph `$orderby`. */
export type SharePointSortOption = "asc" | "desc" | "lastModified";

const GRAPH_ORIGIN = "https://graph.microsoft.com";
const GRAPH_URL = `${GRAPH_ORIGIN}/v1.0`;
/** Only the fields the browser renders; keeps payloads small on large libraries. */
const DRIVE_ITEM_SELECT = "id,name,folder,file,size,lastModifiedDateTime,webUrl,parentReference";

const ORDER_BY: Record<SharePointSortOption, string> = {
    asc: "name asc",
    desc: "name desc",
    lastModified: "lastModifiedDateTime desc",
};

/**
 * Browses SharePoint Online drive items via Microsoft Graph with a token from SharePointAuthService.
 *
 * A connection's root comes straight from project settings (`ProjectFolderConnection.driveId` /
 * `itemId`), so no site or URL lookup is needed. Listings are paged (`$top`) and sorted server-side
 * so large libraries never have to be drained or sorted client-side.
 */
export class SharePointGraphService {
    private readonly retryAfterHeaderKey = "Retry-After";
    private readonly defaultRetryTimeInSeconds = 1;
    private readonly maxRetryAttempts = 5;
    private readonly pageSize = 200;

    constructor(
        private sharePointAuthService: SharePointAuthService,
        private httpRequestWrapper: HttpRequestWrapper,
        private logger: Logger
    ) {}

    /** The drive item to list for a connection's root; `itemId` null means the library root. */
    async resolveRoot(connection: ProjectFolderConnection): Promise<SharePointDriveLocation> {
        if (!connection.driveId) {
            throw new SharePointGraphError(
                `SharePoint connection "${connection.name}" has no drive id in project settings`,
                "notConfigured"
            );
        }
        return { driveId: connection.driveId, itemId: connection.itemId ?? null };
    }

    /** Lists one page of a folder; pass the previous page's `nextLink` to continue. */
    async getFolderChildrenPage(
        location: SharePointDriveLocation,
        sort: SharePointSortOption = "asc",
        nextLink?: string
    ): Promise<SharePointFolderPage> {
        const url = nextLink
            ? SharePointGraphService.assertGraphUrl(nextLink)
            : `${SharePointGraphService.itemUrl(location)}/children` +
              `?$top=${this.pageSize}&$select=${DRIVE_ITEM_SELECT}&$orderby=${encodeURIComponent(ORDER_BY[sort])}`;
        const response: any = await this.getWithRetry(url);
        return {
            items: (response?.value ?? []).map((item: any) => this.mapDriveItem(item)),
            nextLink: response?.["@odata.nextLink"] ?? undefined,
        };
    }

    /** Graph addresses a library root as `/root`, not `/items/{id}`. */
    private static itemUrl(location: SharePointDriveLocation): string {
        const drive = `${GRAPH_URL}/drives/${encodeURIComponent(location.driveId)}`;
        return location.itemId ? `${drive}/items/${encodeURIComponent(location.itemId)}` : `${drive}/root`;
    }

    /** `@odata.nextLink` comes from the response body; never follow it anywhere but Graph. */
    private static assertGraphUrl(url: string): string {
        if (!url.startsWith(`${GRAPH_ORIGIN}/`)) {
            throw new SharePointGraphError("Refusing to follow a pagination link outside Microsoft Graph", "unknown");
        }
        return url;
    }

    private mapDriveItem(item: any): SharePointFolderItem {
        return {
            id: item.id,
            name: item.name,
            type: item.folder ? "folder" : "file",
            lastModified: item.lastModifiedDateTime,
            webUrl: item.webUrl,
            parentDriveId: item.parentReference?.driveId,
            mimeType: item.file?.mimeType,
            sizeInBytes: item.size,
        };
    }

    private async getWithRetry(url: string, attemptCount: number = 1): Promise<any> {
        try {
            const accessToken = await this.sharePointAuthService.getAccessToken();
            return await this.httpRequestWrapper.get(url, "json", { Authorization: `Bearer ${accessToken}` });
        } catch (error) {
            if (attemptCount >= this.maxRetryAttempts) {
                this.logger.error(
                    `SharePointGraphService: max retry attempts (${this.maxRetryAttempts}) reached for request`,
                    error
                );
                throw this.toTypedError(error);
            }

            const status = (error as any)?.status;

            if (status === 429) {
                const retryAfter =
                    ((error as any).getResponseHeader?.(this.retryAfterHeaderKey) || this.defaultRetryTimeInSeconds) *
                    1000;
                this.logger.info(
                    `SharePointGraphService: throttled (429) on attempt ${attemptCount}, retrying after ${retryAfter}ms`
                );
                await new Promise((resolve) => setTimeout(resolve, retryAfter));
                return this.getWithRetry(url, attemptCount + 1);
            }

            if (status === 401) {
                this.logger.info(
                    `SharePointGraphService: got 401 on attempt ${attemptCount}, retrying with a fresh token`
                );
                // Drop the in-memory token so getAccessToken re-acquires instead of handing back the
                // same rejected one (which would just 401 again until max attempts).
                this.sharePointAuthService.invalidateCachedToken();
                return this.getWithRetry(url, attemptCount + 1);
            }

            throw this.toTypedError(error);
        }
    }

    private toTypedError(error: any): SharePointGraphError {
        if (error instanceof SharePointGraphError) {
            return error;
        }
        const status = error?.status;
        if (status === 403) {
            return new SharePointGraphError("Insufficient permissions to access SharePoint", "forbidden");
        }
        if (status === 404) {
            return new SharePointGraphError("SharePoint folder was not found", "notFound");
        }
        if (status === 429) {
            return new SharePointGraphError("SharePoint requests are being throttled", "throttled");
        }
        return new SharePointGraphError("Failed to communicate with SharePoint via Microsoft Graph", "network");
    }
}
